import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  CheckCircle, Target, Users, MessageSquare, Star, Calendar, Share2, 
  BarChart3, Zap, Shield, Clock, Smartphone, AlertTriangle, XCircle,
  TrendingUp, Phone, Mail, Bot, DollarSign, Award, ArrowRight
} from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';

const FeaturesPage = () => {
  const { formatPrice } = useCurrency();

  const problems = [
    {
      icon: XCircle,
      color: 'red',
      problem: 'Leads Slipping Through the Cracks',
      stat: '73%',
      description: 'of leads never get followed up because you\'re too busy or forget',
      solution: '24/7 Lead Capture & Auto-Response',
      solutionDetails: 'Every lead is captured instantly and gets an immediate response - even at 3am on Sunday'
    },
    {
      icon: Phone,
      color: 'orange',
      problem: 'Missed Calls = Lost Revenue',
      stat: '62%',
      description: 'of callers won\'t call back if you miss their call',
      solution: 'Missed Call Text-Back',
      solutionDetails: 'Automatic SMS sent within seconds when you miss a call, capturing the lead before they call your competitor'
    },
    {
      icon: Clock,
      color: 'yellow',
      problem: 'Time Wasted on Admin',
      stat: '15hrs',
      description: 'per week spent on repetitive admin tasks',
      solution: 'Complete Automation',
      solutionDetails: 'Booking confirmations, reminders, follow-ups, and invoices all happen automatically'
    },
    {
      icon: Calendar,
      color: 'blue',
      problem: 'No-Shows Costing You Money',
      stat: '20%',
      description: 'of appointments result in no-shows without reminders',
      solution: 'Smart Reminder System',
      solutionDetails: 'Automated SMS and email reminders reduce no-shows by up to 85%'
    },
    {
      icon: Star,
      color: 'purple',
      problem: 'Not Enough Google Reviews',
      stat: '92%',
      description: 'of businesses never ask customers for reviews',
      solution: 'Review Automation',
      solutionDetails: 'Automatically request 5-star reviews after every job - we\'ve generated over 150,000 reviews for our clients'
    },
    {
      icon: Users,
      color: 'green',
      problem: 'One-Time Customers Only',
      stat: '68%',
      description: 'of customers never return because you don\'t stay in touch',
      solution: 'Nurture Campaigns',
      solutionDetails: 'Automated email and SMS sequences keep you top-of-mind and bring customers back'
    }
  ];

  const coreFeatures = [
    {
      category: 'Lead Capture',
      icon: Target,
      color: 'from-orange-500 to-red-500',
      features: [
        'Unlimited landing pages with drag-and-drop builder',
        'Smart web forms that capture every detail',
        'Live chat widget for instant engagement',
        'Facebook & Google lead capture integration',
        'QR code generators for offline to online',
        'Exit-intent popups to capture abandoning visitors'
      ]
    },
    {
      category: 'CRM & Pipeline',
      icon: Users,
      color: 'from-blue-500 to-indigo-500',
      features: [
        'Visual sales pipeline with drag-and-drop',
        'Complete contact history and timeline',
        'Deal tracking and forecasting',
        'Custom fields for your business needs',
        'Lead scoring and prioritization',
        'Automated task creation and assignment'
      ]
    },
    {
      category: 'Communication',
      icon: MessageSquare,
      color: 'from-green-500 to-teal-500',
      features: [
        'Unified inbox for SMS, email, and chat',
        'Two-way SMS conversations',
        'Email marketing campaigns',
        'Pre-built message templates',
        'Bulk messaging capabilities',
        'Smart AI-powered responses'
      ]
    },
    {
      category: 'Automation',
      icon: Zap,
      color: 'from-yellow-500 to-orange-500',
      features: [
        'Visual workflow builder (no coding)',
        'Trigger-based automation',
        'Multi-channel drip campaigns',
        'Automated follow-up sequences',
        'Birthday and anniversary campaigns',
        'Re-engagement automation for lost leads'
      ]
    },
    {
      category: 'Booking & Scheduling',
      icon: Calendar,
      color: 'from-purple-500 to-pink-500',
      features: [
        'Online booking calendar with availability',
        'Automated appointment confirmations',
        'SMS and email reminders',
        'Calendar sync (Google, Outlook)',
        'Buffer times and booking rules',
        'Payment collection at booking'
      ]
    },
    {
      category: 'Reputation Management',
      icon: Star,
      color: 'from-amber-500 to-yellow-500',
      features: [
        'Automated review request campaigns',
        'Multi-platform review monitoring',
        'Review response templates',
        'Google My Business integration',
        'Negative review filtering',
        'Review showcase widgets for your website'
      ]
    },
    {
      category: 'Analytics & Reporting',
      icon: BarChart3,
      color: 'from-cyan-500 to-blue-500',
      features: [
        'Real-time dashboard with key metrics',
        'Revenue and conversion tracking',
        'Lead source attribution',
        'ROI calculator and reporting',
        'Team performance analytics',
        'Custom report builder'
      ]
    },
    {
      category: 'Social Media',
      icon: Share2,
      color: 'from-pink-500 to-rose-500',
      features: [
        'Multi-platform post scheduling',
        'Content calendar view',
        'Post templates and ideas',
        'Social inbox monitoring',
        'Engagement tracking',
        'Best time to post recommendations'
      ]
    }
  ];

  const additionalBenefits = [
    {
      title: 'Mobile-First Design',
      icon: Smartphone,
      description: 'Run your entire business from your phone with our intuitive mobile app'
    },
    {
      title: 'Enterprise Security',
      icon: Shield,
      description: 'Bank-level encryption, GDPR compliant, and daily backups keep your data safe'
    },
    {
      title: '5-Minute Setup',
      icon: Clock,
      description: 'Pre-built templates mean you can be up and running before your coffee gets cold'
    },
    {
      title: 'World-Class Support',
      icon: Award,
      description: 'Email, phone, and live chat support from real humans who care about your success'
    }
  ];

  const getColorClass = (color) => {
    const colors = {
      red: 'text-red-500',
      orange: 'text-orange-500',
      yellow: 'text-yellow-500',
      blue: 'text-blue-500',
      purple: 'text-purple-500',
      green: 'text-green-500'
    };
    return colors[color] || 'text-gray-500';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0E163B] via-[#1a2654] to-[#0E163B] text-white py-24 px-4 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ 
            backgroundImage: 'radial-gradient(circle at 20px 20px, white 2px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#FF5A3D] rounded-full opacity-10 blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#FF8C42] rounded-full opacity-10 blur-3xl"></div>

        <div className="max-w-7xl mx-auto text-center relative z-10">
          {/* Alert Badge */}
          <div className="inline-flex items-center bg-red-500/20 border border-red-400/30 px-6 py-3 rounded-full mb-8">
            <AlertTriangle size={20} className="text-red-400 mr-2" />
            <span className="font-semibold text-red-400">You're losing 73% of potential revenue</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            6 Problems Killing Your Profits
            <br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42]">
              (And How We Fix Them)
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed">
            Every local business faces the same profit-killing problems. The difference? 
            Successful businesses automate solutions instead of drowning in admin.
          </p>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto mb-12">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
              <div className="text-4xl font-bold text-[#FF5A3D] mb-2">73%</div>
              <div className="text-sm text-gray-300">Leads Never Followed Up</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
              <div className="text-4xl font-bold text-[#FF5A3D] mb-2">15hrs</div>
              <div className="text-sm text-gray-300">Wasted on Admin Weekly</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
              <div className="text-4xl font-bold text-[#FF5A3D] mb-2">20%</div>
              <div className="text-sm text-gray-300">No-Show Rate</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
              <div className="text-4xl font-bold text-[#FF5A3D] mb-2">92%</div>
              <div className="text-sm text-gray-300">Never Ask for Reviews</div>
            </div>
          </div>

          <Button 
            asChild
            className="bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white font-bold text-xl px-12 py-7 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-2xl"
          >
            <a href="/pricing" className="flex items-center">
              Fix All 6 Problems for {formatPrice(5)}
              <ArrowRight className="ml-2" size={24} />
            </a>
          </Button>
        </div>
      </section>

      {/* Problems & Solutions Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#FF5A3D]/10 text-[#FF5A3D] px-4 py-2 mb-4">
              PROBLEMS → SOLUTIONS
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0E163B] mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              The 6 Biggest Profit-Killers
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These problems are costing you thousands every month. Here's how we solve each one.
            </p>
          </div>

          <div className="space-y-8">
            {problems.map((item, index) => (
              <Card key={index} className="border-2 border-gray-200 hover:border-[#FF5A3D] hover:shadow-2xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Problem Side */}
                    <div className="border-r border-gray-200 pr-8">
                      <div className="flex items-start space-x-4 mb-4">
                        <div className={`p-3 rounded-xl bg-${item.color}-100`}>
                          <item.icon size={32} className={getColorClass(item.color)} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <Badge className="bg-red-500 text-white">PROBLEM #{index + 1}</Badge>
                            <span className={`text-3xl font-bold ${getColorClass(item.color)}`}>{item.stat}</span>
                          </div>
                          <h3 className="text-2xl font-bold text-[#0E163B] mb-2">{item.problem}</h3>
                          <p className="text-gray-600">{item.description}</p>
                        </div>
                      </div>
                    </div>

                    {/* Solution Side */}
                    <div className="pl-0 md:pl-8">
                      <div className="flex items-start space-x-4">
                        <div className="p-3 rounded-xl bg-green-100">
                          <CheckCircle size={32} className="text-green-600" />
                        </div>
                        <div className="flex-1">
                          <Badge className="bg-green-500 text-white mb-3">OUR SOLUTION</Badge>
                          <h3 className="text-2xl font-bold text-[#0E163B] mb-2">{item.solution}</h3>
                          <p className="text-gray-600 mb-4">{item.solutionDetails}</p>
                          <Button 
                            asChild
                            variant="outline" 
                            className="border-[#FF5A3D] text-[#FF5A3D] hover:bg-[#FF5A3D] hover:text-white"
                          >
                            <a href="/pricing">
                              Get This Solution →
                            </a>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Core Features Grid */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-600 px-4 py-2 mb-4">
              COMPLETE TOOLKIT
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0E163B] mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Everything You Need in One Platform
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Stop paying for 5 different tools. Get everything in one simple, powerful system.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {coreFeatures.map((feature, index) => (
              <Card key={index} className="border-2 border-gray-200 hover:shadow-2xl transition-all duration-300 group">
                <CardContent className="p-8">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className={`p-4 rounded-2xl bg-gradient-to-r ${feature.color} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <feature.icon size={32} className="text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#0E163B]">{feature.category}</h3>
                  </div>
                  <ul className="space-y-3">
                    {feature.features.map((item, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <CheckCircle size={20} className="text-[#FF5A3D] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Benefits */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0E163B] mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Plus These Extra Benefits
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {additionalBenefits.map((benefit, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-all duration-300">
                <CardContent className="p-8">
                  <div className="w-20 h-20 bg-gradient-to-br from-[#FF5A3D] to-[#FF8C42] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                    <benefit.icon size={40} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0E163B] mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-gradient-to-r from-[#FF5A3D] via-[#FF6B47] to-[#FF8C42] text-white relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <TrendingUp size={64} className="mx-auto mb-6" />
          <h2 className="text-4xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Ready to Fix These Problems?
          </h2>
          <p className="text-xl md:text-2xl mb-10 opacity-95 leading-relaxed">
            Join thousands of local businesses who've automated their way to success
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              asChild
              className="bg-white text-[#FF5A3D] hover:bg-gray-100 font-bold text-xl px-12 py-7 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-2xl"
            >
              <a href="/pricing">
                Start 14-Day Trial for {formatPrice(5)}
              </a>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#FF5A3D] font-semibold text-xl px-12 py-7 rounded-xl transition-all duration-300"
            >
              <a href="/contact">
                Talk to an Expert
              </a>
            </Button>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <div className="flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <CheckCircle size={16} className="mr-2" />
              <span>Setup in 5 Minutes</span>
            </div>
            <div className="flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <CheckCircle size={16} className="mr-2" />
              <span>No Long-Term Contract</span>
            </div>
            <div className="flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <CheckCircle size={16} className="mr-2" />
              <span>Cancel Anytime</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FeaturesPage;
